/**
 * M3.1 (Juni 2026) – OPS Shadow Validation Dashboard (READ-ONLY).
 *
 * Reine Betreiber-Sicht auf den Cutover-Zustand: KPI-Karten + Detailtabelle mit Filter.
 * Zeigt ausschließlich Ergebnisse der bestehenden Shadow-Validierung an (Legacy↔Resolver).
 * KEINE Writes, KEINE Billing-/Resolver-Änderung – nur Anzeige.
 */
import { useCallback, useEffect, useState } from "react";

import { AlertTriangle, Loader2, RefreshCw, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { opsApi, opsErrorMessage } from "@/lib/opsApi";

const STATUS_STYLE = {
  identical: "bg-emerald-100 text-emerald-800 border-emerald-200",
  different: "bg-red-100 text-red-800 border-red-200",
  excluded: "bg-slate-100 text-slate-600 border-slate-200",
  missing_contract: "bg-amber-100 text-amber-900 border-amber-200",
};
const STATUS_LABEL = {
  identical: "Identisch",
  different: "Abweichung",
  excluded: "Ausgeschlossen",
  missing_contract: "Vertrag fehlt",
};

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "different", label: "Nur Unterschiede" },
  { key: "excluded", label: "Nur ausgeschlossen" },
  { key: "missing_contract", label: "Nur fehlende Verträge" },
];

function Kpi({ id, label, value, tone }) {
  const toneCls =
    tone === "red"
      ? "text-red-700"
      : tone === "green"
        ? "text-emerald-700"
        : tone === "amber"
          ? "text-amber-700"
          : "text-slate-900";
  return (
    <div
      data-testid={`ops-shadow-kpi-${id}`}
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
    >
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value ?? "–"}</div>
    </div>
  );
}

export function ShadowValidationSection() {
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback((activeFilter) => {
    setLoading(true);
    setLoadErr("");
    Promise.all([opsApi.shadowSummary(), opsApi.shadowDetails(activeFilter)])
      .then(([s, d]) => {
        setSummary(s);
        setDetails(d.details || []);
      })
      .catch((e) => setLoadErr(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filter);
  }, [load, filter]);

  return (
    <Card
      className="bg-white border border-slate-200 shadow-sm overflow-hidden"
      data-testid="ops-shadow-validation-section"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Billing → Shadow-Validierung
            </h2>
            <p className="text-xs text-slate-500">
              Read-only Cutover-Status: neuer Resolver parallel gegen die bestehende Logik.
              Erst wenn „Abweichung" auf 0 steht, ist der M3-Cutover möglich.
            </p>
          </div>
        </div>
        <Button
          data-testid="ops-shadow-refresh"
          variant="outline"
          size="sm"
          onClick={() => load(filter)}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {/* KPI-Karten */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 px-5 py-4 bg-slate-50/60">
        <Kpi id="total" label="Mandanten" value={summary?.total_tenants} />
        <Kpi id="compared" label="Vergleichbar" value={summary?.compared} />
        <Kpi
          id="diff"
          label="Abweichung"
          value={summary?.diff_count}
          tone={summary?.diff_count > 0 ? "red" : "green"}
        />
        <Kpi id="identical" label="Identisch" value={summary?.diff_free} tone="green" />
        <Kpi id="excluded" label="Ausgeschlossen" value={summary?.excluded} />
        <Kpi
          id="contracts-missing"
          label="Verträge fehlen"
          value={summary?.contracts_missing}
          tone={summary?.contracts_missing > 0 ? "amber" : "green"}
        />
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-100">
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            data-testid={`ops-shadow-filter-${f.key}`}
            variant={filter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
          >
            {f.label}
          </Button>
        ))}
        {summary && (
          <span className="ml-auto text-xs text-slate-400" data-testid="ops-shadow-flag">
            Shadow-Flag: {summary.shadow_flag_enabled ? "aktiv" : "aus"}
          </span>
        )}
      </div>

      {loading && (
        <div
          className="px-5 py-10 flex items-center justify-center text-slate-400"
          data-testid="ops-shadow-loading"
        >
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && loadErr && (
        <div className="px-5 py-6" data-testid="ops-shadow-error">
          <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {loadErr}
            </p>
            <Button variant="outline" size="sm" onClick={() => load(filter)}>
              Erneut versuchen
            </Button>
          </div>
        </div>
      )}

      {!loading && !loadErr && (
        <div className="overflow-x-auto">
          <Table data-testid="ops-shadow-table">
            <TableHeader>
              <TableRow>
                <TableHead>Mandant</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Legacy-Tarif</TableHead>
                <TableHead>Vertrag</TableHead>
                <TableHead>Resolverfähig</TableHead>
                <TableHead>Shadow-Status</TableHead>
                <TableHead>Abweichende Felder</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {details.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center text-slate-400 py-8"
                    data-testid="ops-shadow-empty"
                  >
                    Keine Mandanten für diesen Filter.
                  </TableCell>
                </TableRow>
              )}
              {details.map((d, idx) => (
                <TableRow key={d.tenant_id || idx} data-testid={`ops-shadow-row-${idx}`}>
                  <TableCell className="text-sm">
                    <div className="font-medium text-slate-900">{d.name || "–"}</div>
                    <div className="text-xs text-slate-400 font-mono">{d.tenant_id}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{d.account_status || "–"}</TableCell>
                  <TableCell className="text-sm text-slate-700">{d.legacy_plan || "–"}</TableCell>
                  <TableCell className="text-sm">
                    {d.contract_present ? (
                      <span className="text-emerald-600 font-medium">ja</span>
                    ) : (
                      <span className="text-slate-400">nein</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {d.resolver_ready ? (
                      <span className="text-emerald-600 font-medium">ja</span>
                    ) : (
                      <span className="text-slate-400">nein</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    <Badge
                      variant="outline"
                      data-testid={`ops-shadow-status-${d.shadow_status}`}
                      className={`rounded-md font-medium px-2.5 py-1 text-xs border ${
                        STATUS_STYLE[d.shadow_status] || "bg-slate-100 text-slate-700 border-slate-200"
                      }`}
                    >
                      {STATUS_LABEL[d.shadow_status] || d.shadow_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {(d.diff_fields && d.diff_fields.length > 0) ? d.diff_fields.join(", ") : "–"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
